import { createRouter, createWebHistory, isNavigationFailure } from "vue-router";

import { useExamSettings } from "@/common/composables/use-exam-settings";
import config from "@/common/config";
import { hasAuthenticatedCookie } from "@/common/has-authenticated-cookie";
import { buildPackageAndLangPath } from "@/common/route-path-helpers";
import { i18n } from "@/i18n";
import { useAuthStore, useMenuStore, useSettingsStore } from "@/stores";
import AboutView from "@/views/AboutView.vue";
import Article from "@/views/Article.vue";
import DownloadExamInformation from "@/views/DownloadExamInformation.vue";
import DsnSpellingRules from "@/views/DsnSpellingRules.vue";
import ExamPreparation from "@/views/ExamPreparation.vue";
import GetAccessToday from "@/views/GetAccessToday.vue";
import Home from "@/views/HomeView.vue";
import IframeLoginView from "@/views/IframeLoginView.vue";
import IframeWaitingForAuthenticationView from "@/views/IframeWaitingForAuthenticationView.vue";
import InflectionTrainer from "@/views/InflectionTrainer.vue";
import Institutions from "@/views/Institutions.vue";
import IrregularVerbs from "@/views/IrregularVerbs.vue";
import Login from "@/views/LoginView.vue";
import LogoutView from "@/views/LogoutView.vue";
import NoAccount from "@/views/NoAccount.vue";
import NotFoundView from "@/views/NotFoundView.vue";
import OpgavehaefterView from "@/views/OpgavehaefterView.vue";
import Policies from "@/views/Policies.vue";
import RedirectFromOrdbogen from "@/views/RedirectFromOrdbogen.vue";
import SelfClosingWindow from "@/views/SelfClosingWindow.vue";
import Settings from "@/views/Settings.vue";
import UniloginUnavailable from "@/views/UniloginUnavailableView.vue";
import VocabAchievements from "@/views/VocabAchievements.vue";
import VocabClassQuiz from "@/views/VocabClassQuiz.vue";
import VocabHome from "@/views/VocabHome.vue";
import VocabList from "@/views/VocabList.vue";
import VocabQuiz from "@/views/VocabQuiz.vue";
import VocabUnavailable from "@/views/VocabUnavailable.vue";
import Workshop from "@/views/Workshop.vue";

import {
  authGuard,
  guestGuard,
  iframeGuard,
  loginGuard,
  noAuthGuard,
  syncPackageAndLangGuard,
  teacherGuard
} from "./guards";
import vocabUnavailable from "./guards/vocabUnavailable";
import {
  QUERY_ARTICLE_TAB_KEY,
  QUERY_DSN_PARAGRAPH_KEY,
  QUERY_INFLECTION_SCHEME_ID_KEY,
  QUERY_SEARCH_DICTIONARY_KEY,
  QUERY_SEARCH_DIRECTION_KEY,
  QUERY_SEARCH_FORCE_DIRECTION_KEY,
  QUERY_SEARCH_WORD_KEY
} from "./query-params";

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: "/",
      component: AboutView,
      children: [
        {
          path: "",
          name: "home",
          component: Home,
          beforeEnter: noAuthGuard
        },
        {
          path: "/about",
          name: "about",
          component: AboutView,
          meta: {
            disableLoader: true
          }
        }
      ]
    },
    {
      path: "/dictionary",
      component: Home,
      name: "dictionary",
      meta: {
        forceLanguageSelection: true
      },
      beforeEnter: authGuard
    },
    {
      path: "/redirect-from-ordbogen",
      component: RedirectFromOrdbogen,
      name: "redirect-from-ordbogen",
      meta: {
        disableLoader: true
      }
    },
    {
      path: "/no-account",
      component: NoAccount,
      name: "no-account",
      meta: {
        disableLoader: true
      },
      beforeEnter: guestGuard
    },
    {
      path: "/unilogin-unavailable",
      component: UniloginUnavailable,
      name: "unilogin-unavailable",
      meta: {
        disableLoader: true
      },
      beforeEnter: guestGuard
    },
    {
      path: "/get-access-today",
      component: GetAccessToday,
      name: "get-access-today",
      meta: {
        disableLoader: true
      },
      beforeEnter: guestGuard
    },
    {
      path: "/login",
      component: Login,
      beforeEnter: [loginGuard, guestGuard],
      children: [
        {
          path: "",
          name: "login",
          component: Login,
          meta: {
            disallowLoadingUserData: true
          }
        },
        {
          path: "/login-from-ordbogen",
          name: "login-from-ordbogen",
          component: Login,
          meta: {
            disableLoader: true
          }
        }
      ]
    },
    {
      path: "/logout",
      component: LogoutView,
      name: "logout",
      meta: {
        disableOnboarding: true
      },
      beforeEnter: authGuard
    },
    {
      path: buildPackageAndLangPath("article"),
      component: Article,
      props: (route) => ({
        searchWord: route.query[QUERY_SEARCH_WORD_KEY],
        searchDictionary: route.query[QUERY_SEARCH_DICTIONARY_KEY],
        searchDirection: route.query[QUERY_SEARCH_DIRECTION_KEY],
        searchDirectionForced: Boolean(route.query[QUERY_SEARCH_FORCE_DIRECTION_KEY]),
        inflectionSchemeId: route.query[QUERY_INFLECTION_SCHEME_ID_KEY],
        searchTab: route.query[QUERY_ARTICLE_TAB_KEY]
      }),
      name: "article",
      meta: {
        forceLanguageSelection: true
      },
      beforeEnter: [authGuard, syncPackageAndLangGuard]
    },
    {
      path: buildPackageAndLangPath("workshop"),
      component: Workshop,
      name: "workshop",
      beforeEnter: [authGuard, syncPackageAndLangGuard]
    },
    {
      path: buildPackageAndLangPath("workshop/dsn-spelling-rules"),
      component: DsnSpellingRules,
      props: (route) => ({
        activeParagraph: route.query[QUERY_DSN_PARAGRAPH_KEY]
      }),
      name: "workshop.dsn-spelling-rules",
      beforeEnter: [authGuard, syncPackageAndLangGuard],
      meta: {
        breadcrumbs: [
          {
            text: i18n.global.t("menu.workshop"),
            route: "workshop"
          },
          {
            text: i18n.global.t("menu.workshopItems.rules"),
            route: "workshop.dsn-spelling-rules"
          }
        ]
      }
    },
    {
      path: buildPackageAndLangPath("workshop/irregular-verbs"),
      component: IrregularVerbs,
      name: "workshop.irregular-verbs",
      beforeEnter: [authGuard, syncPackageAndLangGuard],
      meta: {
        breadcrumbs: [
          {
            text: i18n.global.t("menu.workshop"),
            route: "workshop"
          },
          {
            text: i18n.global.t("menu.workshopItems.irregularVerbs"),
            route: "workshop.irregular-verbs"
          }
        ]
      }
    },
    {
      path: buildPackageAndLangPath("workshop/inflection-trainer"),
      component: InflectionTrainer,
      name: "workshop.inflection-trainer",
      beforeEnter: [authGuard, syncPackageAndLangGuard],
      meta: {
        breadcrumbs: [
          {
            text: i18n.global.t("menu.workshop"),
            route: "workshop"
          },
          {
            text: i18n.global.t("menu.workshopItems.inflectionTrainer"),
            route: "workshop.inflection-trainer"
          }
        ]
      }
    },
    {
      path: "/institutions",
      component: Institutions,
      name: "institutions",
      meta: {
        disableOnboarding: true
      },
      beforeEnter: authGuard
    },
    {
      path: "/settings/general",
      component: Settings,
      name: "settings.general",
      redirect: () => {
        const settingsStore = useSettingsStore();
        const packageId = settingsStore.preferredPackageGroup?.id;
        const defaultPage = packageId ?? "accessibility";

        return `/settings/${defaultPage}`;
      }
    },
    {
      path: "/settings/:setting",
      component: Settings,
      name: "settings",
      beforeEnter: authGuard
    },
    {
      path: "/policies/:policy?",
      component: Policies,
      name: "policies"
    },

    // Exam information routes
    {
      path: "/download-exam-information",
      component: DownloadExamInformation,
      name: "download-exam-information",
      meta: {
        disableLoader: true,
        layout: "MinimalLayout"
      }
    },
    {
      path: "/exam-preparation",
      component: ExamPreparation,
      name: "exam-preparation",
      meta: {
        layout: "LandingPageLayout"
      }
    },
    {
      path: "/opgavehaefter",
      component: OpgavehaefterView,
      name: "opgavehaefter",
      meta: {
        layout: "LandingPageLayout"
      }
    },

    // Iframe related routes
    {
      path: "/iframe/login",
      name: "iframe.login",
      component: IframeLoginView,
      meta: {
        disableLoader: true,
        isIframeRoute: true,
        layout: "MinimalLayout"
      }
    },
    {
      path: "/iframe/login-success",
      component: SelfClosingWindow,
      beforeEnter: authGuard,
      meta: {
        disableLoader: true,
        layout: "MinimalLayout"
      }
    },
    {
      path: "/iframe/waiting-for-authentication",
      name: "iframe.waiting-for-authentication",
      component: IframeWaitingForAuthenticationView,
      meta: {
        disableLoader: true,
        isIframeRoute: true,
        layout: "MinimalLayout"
      }
    },

    // Exam settings routes
    {
      path: "/e",
      component: Home,
      beforeEnter: async (to, from, next) => {
        const examSettings = useExamSettings();
        await examSettings.enableSearchAssistance();

        return next("/");
      }
    },
    {
      path: "/f",
      component: Home,
      beforeEnter: async (to, from, next) => {
        const examSettings = useExamSettings();
        await examSettings.enableForeignDictionaries();

        return next("/");
      }
    },

    // Vocabulary trainer routes
    {
      path: "/vocab",
      component: VocabHome,
      name: "vocab.home",
      beforeEnter: [authGuard, vocabUnavailable],
      meta: {
        isVocabularyTrainerRoute: true
      }
    },
    {
      path: "/vocab/lists/:shareCode",
      component: VocabList,
      name: "vocab.list",
      beforeEnter: [authGuard, vocabUnavailable],
      meta: {
        isVocabularyTrainerRoute: true
      }
    },
    {
      path: "/vocab/lists/:shareCode/quizzes/:quizCode",
      component: VocabQuiz,
      name: "vocab.quiz",
      beforeEnter: [authGuard, vocabUnavailable],
      meta: {
        isVocabularyTrainerRoute: true,
        layout: "VocabQuizLayout"
      }
    },
    {
      path: "/vocab/lists/:shareCode/class-quizzes/:quizCode",
      component: VocabClassQuiz,
      name: "vocab.classQuiz",
      beforeEnter: [authGuard, vocabUnavailable],
      meta: {
        isVocabularyTrainerRoute: true,
        layout: "VocabClassQuizLayout"
      }
    },
    {
      path: "/vocab/lists/:shareCode/class-quizzes/:quizCode/stats",
      component: VocabClassQuiz,
      name: "vocab.classQuiz.stats",
      beforeEnter: [authGuard, vocabUnavailable, teacherGuard],
      meta: {
        isVocabularyTrainerRoute: true,
        layout: "VocabClassQuizLayout"
      }
    },
    {
      path: "/vocab/achievements",
      component: VocabAchievements,
      name: "vocab.achievements",
      beforeEnter: [authGuard, vocabUnavailable],
      meta: {
        isVocabularyTrainerRoute: true
      }
    },

    {
      path: "/vocab-unavailable",
      component: VocabUnavailable,
      name: "vocab-unavailable",
      beforeEnter: authGuard
    },

    // Catch-all 404 route
    { path: "/:pathMatch(.*)*", name: "not-found", component: NotFoundView }
  ],
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    }

    if (to.hash) {
      return {
        el: to.hash,
        behavior: "smooth"
      };
    }

    if (to.path !== from.path) {
      return { top: 0, left: 0 };
    }
  }
});

router.beforeEach(async (to, from, next) => {
  // This sets the title on each route change
  document.title = config.site.title;

  const authStore = useAuthStore();
  const { hideMenu } = useMenuStore();

  const canLoadUserData = !to.meta.disallowLoadingUserData;

  // Only load user if frontend knows it has been logged in before.
  if (canLoadUserData && !authStore.isLoaded && hasAuthenticatedCookie()) {
    await authStore.loadUser();
  }

  // Hide dropdown menu when the route changes
  hideMenu();

  // Resetting "tab history". On next tab, your focus will start at the top again
  document.querySelector("#app")?.focus?.();

  // Make sure the focus is reset upon route changed
  if (document.activeElement instanceof HTMLElement) {
    document.activeElement?.blur();
  }

  return next();
});

router.beforeEach(iframeGuard);

/**
 * Don't throw errors for navigation failures
 *
 * @see https://router.vuejs.org/guide/advanced/navigation-failures.html#detecting-navigation-failures
 * @see https://github.com/vuejs/vue-router/issues/2881#issuecomment-520554378
 */
["push", "replace"].forEach((method) => {
  const originalMethod = router[method];

  router[method] = function (location, onComplete, onAbort) {
    // If using the callback methods on the router method,
    // we assume the caller will handle any errors.
    if (onComplete || onAbort) {
      return originalMethod.call(this, location, onComplete, onAbort);
    }

    return originalMethod.call(this, location).catch((err) => {
      const navigationFailure = isNavigationFailure(err);

      // In debug mode, we want all errors
      if (navigationFailure && config.debug === true) {
        // eslint-disable-next-line no-console
        console.warn(`The following error "${err}" will be turned off in production!`);
        return;
      }

      // Silence unwanted errors
      if (navigationFailure) {
        return err;
      }

      // Rethrow error
      // eslint-disable-next-line promise/no-return-wrap
      return Promise.reject(err);
    });
  };
});

export default router;
